<template>
  <SoAlert
    v-if="isActive"
    class="mx-5 my-5"
    type="error"
    title="Account placed on Involuntary PAYG journey"
    data-testid="involuntary-payg-banner-alert"
  >
    <SoRow>
      <SoCol :default-width="12">
        <p>
          This account is in the Involuntary PAYG journey. Advise the customer
          that they can avoid this by creating a payment plan, paying off their
          balance, creating a sufficient direct debit or by voluntarily agreeing
          to switch to Smart PAYG
        </p>
      </SoCol>
    </SoRow>
    <SoRow
      v-if="
        canRemove ||
        showAddToMeterExchangeButton ||
        showRemoveFromMeterExchangeButton
      "
    >
      <SoCol :default-width="12" class="d-flex flex-wrap ga-2">
        <SoButton
          v-if="canRemove"
          title="Remove from Involuntary PAYG"
          type="inverted"
          data-testid="involuntary-payg-banner-remove-btn"
          @click="openDialog = true"
        />
        <SoButton
          v-if="showAddToMeterExchangeButton"
          title="Add to meter exchange journey"
          type="inverted"
          data-testid="involuntary-payg-banner-add-to-meter-exchange-btn"
          @click="openAddToMeterExchangeDialog = true"
        />
        <SoButton
          v-if="showRemoveFromMeterExchangeButton"
          title="Remove from meter exchange journey"
          type="inverted"
          data-testid="involuntary-payg-banner-remove-from-meter-exchange-btn"
          @click="openRemoveFromMeterExchangeDialog = true"
        />
      </SoCol>
    </SoRow>
    <RemoveFromInvoluntaryPaygDialog
      v-if="canRemove"
      v-model="openDialog"
      :account-number="accountNumber"
      :loading="removeFromInvoluntaryPaygMutator?.isPending ?? false"
      @confirm="handleConfirm"
    />
    <AddToMeterExchangeJourneyDialog
      v-if="isMeterExchangeJourneyEnabled && canMeterExchange"
      v-model="openAddToMeterExchangeDialog"
      :account-number="accountNumber"
      :loading="addToMeterExchangeMutator?.isPending ?? false"
      @confirm="handleAddToMeterExchangeConfirm"
    />
    <RemoveFromMeterExchangeJourneyDialog
      v-if="isMeterExchangeJourneyEnabled && canMeterExchange"
      v-model="openRemoveFromMeterExchangeDialog"
      :account-number="accountNumber"
      :loading="removeFromMeterExchangeMutator?.isPending ?? false"
      @confirm="handleRemoveFromMeterExchangeConfirm"
    />
  </SoAlert>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoRow from "@/components/SoRow.vue"
  import RemoveFromInvoluntaryPaygDialog from "./RemoveFromInvoluntaryPaygDialog.vue"
  import AddToMeterExchangeJourneyDialog from "./AddToMeterExchangeJourneyDialog.vue"
  import RemoveFromMeterExchangeJourneyDialog from "./RemoveFromMeterExchangeJourneyDialog.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { AuthService } from "@/services/AuthService"
  import { PermissionsService } from "@/services/PermissionsService"
  import { FeaturesService } from "@/services/admin/FeaturesService"

  const METER_EXCHANGE_JOURNEY_FLAG = "TMP_SO_32641_METER_EXCHANGE_JOURNEY"

  export default defineComponent({
    components: {
      SoAlert,
      SoButton,
      SoCol,
      SoRow,
      RemoveFromInvoluntaryPaygDialog,
      AddToMeterExchangeJourneyDialog,
      RemoveFromMeterExchangeJourneyDialog,
    },
    data() {
      const current = useCurrentAccountStore()
      const canRemove = PermissionsService.hasPermission(
        "agent.warrants.remove",
        AuthService.getUserRoles(),
      )
      const canMeterExchange = PermissionsService.hasPermission(
        "agent.warrants.meterExchange",
        AuthService.getUserRoles(),
      )
      return {
        current,
        snackbar: useSnackbarStore(),
        canRemove,
        canMeterExchange,
        removeFromInvoluntaryPaygMutator: canRemove
          ? current.removeFromInvoluntaryPayg()
          : null,
        addToMeterExchangeMutator: canMeterExchange
          ? current.addToMeterExchangeJourney()
          : null,
        removeFromMeterExchangeMutator: canMeterExchange
          ? current.removeFromMeterExchangeJourney()
          : null,
        openDialog: false,
        openAddToMeterExchangeDialog: false,
        openRemoveFromMeterExchangeDialog: false,
      }
    },
    computed: {
      isActive() {
        return (
          this.current.warrantStatus.data &&
          !this.current.involuntaryPaygSwitchTicket.data
        )
      },
      accountNumber(): string {
        return this.current.accountNumber
      },
      hasWorkingSmartMeter(): boolean {
        return (
          this.current.meterpoints.data?.some(
            (meterpoint) => meterpoint.hasWorkingSmartMeter,
          ) ?? false
        )
      },
      isMeterExchangeJourneyEnabled(): boolean {
        return (
          FeaturesService.isFeatureActive(METER_EXCHANGE_JOURNEY_FLAG) ?? false
        )
      },
      showAddToMeterExchangeButton(): boolean {
        return (
          this.isMeterExchangeJourneyEnabled &&
          this.canMeterExchange &&
          this.current.meterExchangeEligibility.data.isReadyForMeterExchange &&
          !this.hasWorkingSmartMeter
        )
      },
      showRemoveFromMeterExchangeButton(): boolean {
        if (!this.isMeterExchangeJourneyEnabled || !this.canMeterExchange) {
          return false
        }
        const eligibility = this.current.meterExchangeEligibility.data
        return (
          eligibility.hasMeterExchangeTicket &&
          eligibility.canBeRecalledFromExchange
        )
      },
    },
    methods: {
      async handleConfirm() {
        const mutator = this.removeFromInvoluntaryPaygMutator
        if (!mutator) return
        try {
          await mutator.mutateAsync({ accountNumber: this.accountNumber })
          this.snackbar.addSuccess(
            "Account removed from Involuntary PAYG journey",
          )
        } catch {
          this.snackbar.addError(
            "Failed to remove account from Involuntary PAYG journey",
          )
        } finally {
          this.openDialog = false
        }
      },
      async handleAddToMeterExchangeConfirm() {
        try {
          const billingAccountId = this.current.coreId.data
          // Returning inside `try` still runs `finally`, so the dialog closes.
          if (!this.addToMeterExchangeMutator || !billingAccountId) return
          await this.addToMeterExchangeMutator.mutateAsync({
            billingAccountId,
          })
          this.snackbar.addSuccess("Account added to meter exchange journey")
        } catch {
          this.snackbar.addError(
            "Failed to add account to meter exchange journey",
          )
        } finally {
          this.openAddToMeterExchangeDialog = false
        }
      },
      async handleRemoveFromMeterExchangeConfirm() {
        try {
          const billingAccountId = this.current.coreId.data
          // Returning inside `try` still runs `finally`, so the dialog closes.
          if (!this.removeFromMeterExchangeMutator || !billingAccountId) return
          await this.removeFromMeterExchangeMutator.mutateAsync({
            billingAccountId,
          })
          this.snackbar.addSuccess(
            "Account removed from meter exchange journey",
          )
        } catch {
          this.snackbar.addError(
            "Failed to remove account from meter exchange journey",
          )
        } finally {
          this.openRemoveFromMeterExchangeDialog = false
        }
      },
    },
  })
</script>
